<template>
  <div>
    <div class="row form">
      <div class="col-md-3 form-group">
        <jet-label for="search" value="Search" />
        <jet-input id="search" type="text" v-model="searchFilters.search_text"/>
      </div>

      <div v-if="!isRsm" class="col-md-3 form-group">
        <jet-label for="rsm" value="RSM" />
        <select id="rsm" class="form-control" v-model="searchFilters.rsm_id">
          <option value="">Any</option>
          <option v-for="rsm in this.rsms" :key="rsm.first_name" :value="rsm.id">
            {{ rsm.first_name }} {{ rsm.last_name }}
          </option>
        </select>
      </div>

      <div class="col-md-3 form-group">
        <jet-label for="not_ordered_since" value="Last Ordered Before:" />
        <Calendar id="not_ordered_since" class="w-100" v-model="searchFilters.not_ordered_since" :showIcon="true" dateFormat="mm/dd/yy" :selectOtherMonths="true"/>

        <div class="btn-group mt-1">
          <jet-button class="btn-sm bg-white text-secondary" @click="setDateByPeriod('not_ordered_since', 'week')">Week</jet-button>
          <jet-button class="btn-sm bg-white text-secondary" @click="setDateByPeriod('not_ordered_since', 'month')">Month</jet-button>
          <jet-button class="btn-sm bg-white text-secondary" @click="setDateByPeriod('not_ordered_since', 'year')">Year</jet-button>
          <jet-button class="btn-sm bg-white text-secondary" @click="setDateByPeriod('not_ordered_since', 'never')">Never Ordered</jet-button>
        </div>
      </div>

      <div class="col-md-3 form-group">
        <jet-label for="not_active_since" value="Last Active Before:" />
        <Calendar id="not_active_since" class="w-100" v-model="searchFilters.not_active_since" :showIcon="true" dateFormat="mm/dd/yy" :selectOtherMonths="true"/>

        <div class="btn-group mt-1">
          <jet-button class="btn-sm bg-white text-secondary" @click="setDateByPeriod('not_active_since', 'week')">Week</jet-button>
          <jet-button class="btn-sm bg-white text-secondary" @click="setDateByPeriod('not_active_since', 'month')">Month</jet-button>
          <jet-button class="btn-sm bg-white text-secondary" @click="setDateByPeriod('not_active_since', 'year')">Year</jet-button>
          <jet-button class="btn-sm bg-white text-secondary" @click="setDateByPeriod('not_active_since', 'never')">Never Active</jet-button>
        </div>
      </div>

      <div class="col-md-3 form-group">
        <jet-label for="tags" value="Tags:" />
        <Multiselect
          v-model="searchFilters.tags"
          mode="tags"
          :options="tags"
          :searchable="true"
          class="admin-multiselect"
          id="tags"
        />
      </div>
    </div>
    
    <div class="row">
      <div class="col-12">
        <jet-button class="mr-1" btnStyle="dark" @click="clearSearch">Clear</jet-button>
      </div>
    </div>

    <DataTable ref="mtds-index" 
      :value="filteredUsers" :paginator="true" :rows="50"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown" removableSort
      paginatorPosition="both" currentPageReportTemplate="({first} - {last} of {totalRecords})"
      :rowsPerPageOptions="[10, 20, 50, 100]"
      sortField="date" :sortOrder="-1"
      class="table p-datatable-sm admin-datatable"
      tableClass="border-left border-right border-bottom"
      :selection="selectedItems"
       dataKey="id">
      <Column class="button-container" selectionMode="multiple" headerStyle="width: 3em"></Column>
      <Column field="id" header="#" headerStyle="width: 3em"></Column>
      <Column field="status" header="Status" :sortable="true"></Column>
      <Column header="Name" field="name" :sortable="true"></Column>
      <Column header="Company" field="company_name" :sortable="true"></Column>
      <Column field="rsm" header="RSM" :sortable="true"></Column>
      <Column field="created_at" header="Created" :sortable="true">
        <template #body="slotProps">
          {{ moment(slotProps.data.created_at).format('M/D/YY') }}
        </template>
      </Column>
      <Column field="last_order_date" header="Last Order" :sortable="true">
        <template #body="slotProps">
          <span v-if="slotProps.data.last_order_date">
            {{ moment(slotProps.data.last_order_date).format('M/D/YY') }}
          </span>
          <span v-else>
            No Orders
          </span>
        </template>
      </Column>
      <Column field="last_activity_at" header="Last Active" :sortable="true">
        <template #body="slotProps">
          <span v-if="slotProps.data.last_activity_at">
            {{ moment.utc(slotProps.data.last_activity_at).format('M/D/YY') }}
          </span>
          <span v-else>
            No Activity
          </span>
        </template>
      </Column>
      <Column class="button-container text-nowrap" :exportable="false">
        <template #body="slotProps">
          <jet-button class="btn-short m-1 text-nowrap m-1" :goesTo="route('mtd.view', slotProps.data.id)" icon="fas fa-chevron-right" title="View"></jet-button>
          <jet-button class="btn-short text-nowrap m-1" :goesTo="route('mtd.edit', slotProps.data.id)" btnStyle="dark" title="Edit">
            <i class="fas fa-edit"></i>
          </jet-button>
          <jet-button class="btn-short text-nowrap m-1" :goesTo="route('admin.mtd.shop', slotProps.data.id)" btnStyle="dark" icon="fas fa-shopping-cart" title="Checkout As"></jet-button>
          <jet-button class="btn-short text-nowrap m-1" :goesTo="route('mtd.shopas', slotProps.data.id)" btnStyle="dark" icon="fas fa-sign-in-alt" title="Login As"></jet-button>
        </template>
      </Column>
    </DataTable>

  </div>
</template>

<script>
import Icon from '@/Shared/Icon'
import mapValues from 'lodash/mapValues'
import SearchFilter from '@/Shared/SearchFilter'
import FlashMessages from '@/Shared/FlashMessages'
import BreadCrumbs from '@/Shared/BreadCrumbs'
import JetButton from '@/Jetstream/Button'
import JetLabel from '@/Jetstream/Label'
import JetInput from '@/Jetstream/Input'
import JetInputError from '@/Jetstream/InputError'
import moment from 'moment'
import Search from '@/Shared/Admin/Search'
import Calendar from 'primevue/calendar'
import Multiselect from '@vueform/multiselect'
import EditTags from "@/Shared/EditTags";

export default {
  components: {
    EditTags,
    Icon,
    SearchFilter,
    FlashMessages,
    BreadCrumbs,
    JetButton,
    JetLabel,
    JetInput,
    JetInputError,
    Search,
    Calendar,
    Multiselect,
  },
  props: {
    users: Array,
    saleUsers: Array,
    rsms: Array,
    isRsm: Boolean,
    tags: Array,
    selectedUsers: Array,
  },
  emits: ['update:selectedUsers'],
  data() {
    return {
      url: null,
      bitly: null,
      filteredUsers: this.users,
      selectedItems: this.selectedUsers,
      formText: this.$inertia.form({
        _method: 'put',
        content: null,
        selected: null,
        test_number: null
      }),
      searchFilters: this.$inertia.form({
        search_text: '',
        rsm_id: null,
        tags: [],
        not_ordered_since: '',
        not_active_since: '',
      }),
    }
  },
  created() {
    this.moment = moment;
  },
  watch: {
    selectedItems: {
      handler(newValue) {
        this.$emit('update:selectedUsers', newValue);
      }
    },
    selectedUsers: {
      handler(newValue) {
        this.selectedItems = newValue
      }
    },
    searchFilters: {
      handler() {
        this.filteredUsers = this.users.filter((user) => {
          if (this.selectedUsers) this.selectedUsers = [];
          this.$refs['mtds-index'].resetPage();
          
          let passesSearchText = true;
          if (this.searchFilters.search_text) {
            let searchTerm = this.searchFilters.search_text.toLowerCase();
            passesSearchText = user.name.toLowerCase().includes(searchTerm)
              || (user.company_name || '').toLowerCase().includes(searchTerm)
              || (user.email || '').toLowerCase().includes(searchTerm)
              || (user.phone || '').toLowerCase().includes(searchTerm)
              || (user.rsm || '').toLowerCase().includes(searchTerm);
          }

          let passesRsmId = true;
          if (this.searchFilters.rsm_id) {
            passesRsmId = user.rsm_id === this.searchFilters.rsm_id;
          }

          let passesTags = true;
          if (this.searchFilters.tags.length) {
            passesTags = user.tag_ids.some(tagId => this.searchFilters.tags.includes(tagId));
          }

          let passesNotOrderedSince = true;
          if (this.searchFilters.not_ordered_since) {
            if (this.searchFilters.not_ordered_since === 'Never') {
              passesNotOrderedSince = user.last_order_date === null;
            } else {
              passesNotOrderedSince = user.last_order_date !== null
                && moment(user.last_order_date).isBefore(this.searchFilters.not_ordered_since);
            }
          }

          let passesNotActiveSince = true;
          if (this.searchFilters.not_active_since) {
            if (this.searchFilters.not_active_since === 'Never') {
              passesNotActiveSince = user.last_activity_at === null;
            } else {
              passesNotActiveSince = user.last_activity_at !== null
                && moment(user.last_activity_at).isBefore(this.searchFilters.not_active_since);
            }
          }
          
          return passesSearchText
            && passesRsmId
            && passesTags
            && passesNotOrderedSince
            && passesNotActiveSince;
        });
      },
      deep: true,
    }
  },
  computed: {
    selectedUserIds() {
      return this.selectedUsers.map((user) => {
        return user.id;
      });
    }
  },
  methods: {
    clearSearch() {
      this.searchFilters.reset();
    },
    reset() {
      this.form = mapValues(this.form, () => null)
    },
    setDateByPeriod(field, period) {
      let date = 'Never';
      
      if (period !== 'never') {
        date = moment().subtract(1, period).startOf('day').toDate();
      }
      
      this.searchFilters[field] = date;
    },
  },
}
</script>

<style src="@vueform/multiselect/themes/default.css"></style>
